import { useState } from "react";
import { page } from "../page-copy";
import { revealHostProps } from "../reveal-host";

/** Live shadcn-shaped demos — ordinary React, compiled by JetShake. */

function DemoDialog() {
  const [open, setOpen] = useState(false);
  return (
    <div className="live-card">
      <div className="live-card-head">
        <h3 className="live-card-title">Dialog</h3>
        <span className="live-card-tag">Modal</span>
      </div>
      <p className="live-card-blurb">Confirm a destructive action.</p>
      <button type="button" className="shad-btn shad-btn--primary" onClick={() => setOpen(true)}>
        Delete project
      </button>

      {/* Always mounted — avoids jsx()→appendCompiled→render-component. */}
      <div
        className="shad-dialog"
        role="presentation"
        hidden={!open}
        style={{ display: open ? undefined : "none" }}
      >
        <button
          type="button"
          className="shad-dialog-backdrop"
          aria-label="Close"
          onClick={() => setOpen(false)}
        />
        <div
          className="shad-dialog-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
        >
          <h4 id="dialog-title" className="shad-dialog-title">
            Are you absolutely sure?
          </h4>
          <p className="shad-dialog-desc">
            This action cannot be undone. This will permanently delete your
            project and remove your data from our servers.
          </p>
          <div className="shad-dialog-actions">
            <button
              type="button"
              className="shad-btn shad-btn--ghost"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="shad-btn shad-btn--destructive"
              onClick={() => setOpen(false)}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoDropdownMenu() {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  const pick = (label: string) => {
    setPicked(label);
    setOpen(false);
  };

  return (
    <div className={`live-card${open ? " live-card--raised" : ""}`}>
      <div className="live-card-head">
        <h3 className="live-card-title">Dropdown Menu</h3>
        <span className="live-card-tag">Menu</span>
      </div>
      <p className="live-card-blurb">Actions triggered by a button.</p>
      <div className="shad-dd">
        <button
          type="button"
          className="shad-btn shad-btn--ghost"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          Open
        </button>
        <div
          className="shad-dd-menu"
          role="menu"
          hidden={!open}
          style={{ display: open ? undefined : "none" }}
        >
          <div className="shad-dd-group" role="group">
            <div className="shad-dd-label">My Account</div>
            <button
              type="button"
              className="shad-dd-item"
              role="menuitem"
              onClick={() => pick("Profile")}
            >
              Profile
            </button>
            <button
              type="button"
              className="shad-dd-item"
              role="menuitem"
              onClick={() => pick("Billing")}
            >
              Billing
            </button>
          </div>
          <div className="shad-dd-sep" role="separator" />
          <div className="shad-dd-group" role="group">
            <button
              type="button"
              className="shad-dd-item"
              role="menuitem"
              onClick={() => pick("Team")}
            >
              Team
            </button>
            <button
              type="button"
              className="shad-dd-item"
              role="menuitem"
              onClick={() => pick("Subscription")}
            >
              Subscription
            </button>
          </div>
        </div>
      </div>
      <p
        className="shad-dd-status"
        role="status"
        hidden={!picked}
        style={{ display: picked ? undefined : "none" }}
      >
        Selected: {picked}
      </p>
      <p
        className="shad-dd-status shad-dd-status--muted"
        hidden={!!picked}
        style={{ display: picked ? "none" : undefined }}
      >
        Choose an item
      </p>
    </div>
  );
}

function DemoTabs() {
  const [tab, setTab] = useState("overview");

  return (
    <div className="live-card">
      <div className="live-card-head">
        <h3 className="live-card-title">Tabs</h3>
        <span className="live-card-tag">Compound</span>
      </div>
      <p className="live-card-blurb">Switch panels without a virtual DOM.</p>
      <div className="shad-tabs" role="tablist" aria-label="Demo tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "overview"}
          data-active={tab === "overview" ? "" : undefined}
          className="shad-tab"
          onClick={() => setTab("overview")}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "activity"}
          data-active={tab === "activity" ? "" : undefined}
          className="shad-tab"
          onClick={() => setTab("activity")}
        >
          Activity
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "settings"}
          data-active={tab === "settings" ? "" : undefined}
          className="shad-tab"
          onClick={() => setTab("settings")}
        >
          Settings
        </button>
      </div>
      <div className="shad-tab-panel" role="tabpanel">
        <div className="shad-tab-panel-inner">
          <p
            hidden={tab !== "overview"}
            style={{ display: tab === "overview" ? undefined : "none" }}
          >
            Compound components, context and all — lowered to signals.
          </p>
          <p
            hidden={tab !== "activity"}
            style={{ display: tab === "activity" ? undefined : "none" }}
          >
            Keyed lists and conditionals compile; nothing is simulated.
          </p>
          <p
            hidden={tab !== "settings"}
            style={{ display: tab === "settings" ? undefined : "none" }}
          >
            Fine-grained updates. Only what changed hits the DOM.
          </p>
        </div>
      </div>
    </div>
  );
}

function DemoSwitch() {
  const [airplane, setAirplane] = useState(false);
  const [wifi, setWifi] = useState(true);

  return (
    <div className="live-card">
      <div className="live-card-head">
        <h3 className="live-card-title">Switch</h3>
        <span className="live-card-tag">Settings</span>
      </div>
      <p className="live-card-blurb">Fine-grained updates on toggle.</p>
      <div className="shad-switch-list">
        <div className="shad-switch-row">
          <div>
            <p className="shad-switch-title">Airplane mode</p>
            <p className="shad-switch-desc">Disable radios on this device.</p>
          </div>
          <button
            type="button"
            className="shad-switch"
            role="switch"
            aria-checked={airplane}
            data-on={airplane ? "" : undefined}
            onClick={() => setAirplane(!airplane)}
          >
            <span className="shad-switch-thumb" />
          </button>
        </div>
        <div className="shad-switch-row">
          <div>
            <p className="shad-switch-title">Wi‑Fi</p>
            <p className="shad-switch-desc">Connect to available networks.</p>
          </div>
          <button
            type="button"
            className="shad-switch"
            role="switch"
            aria-checked={wifi}
            data-on={wifi ? "" : undefined}
            onClick={() => setWifi(!wifi)}
          >
            <span className="shad-switch-thumb" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LiveDemos({ delay }: { delay: number }) {
  const { live } = page;
  const reveal = revealHostProps(delay);
  return (
    <div
      className={`${reveal.className} live`}
      style={reveal.style}
      onAnimationEnd={reveal.onAnimationEnd}
    >
      <h2 className="live-heading">
        <span className="live-heading-primary">{live.heading.primary}</span>{" "}
        <span className="live-heading-secondary">{live.heading.secondary}</span>
      </h2>
      <p className="live-lead">{live.lead}</p>
      <div className="live-grid">
        <DemoDialog />
        <DemoDropdownMenu />
        <DemoTabs />
        <DemoSwitch />
      </div>
    </div>
  );
}
